import { Component, OnInit, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
  Event,
} from '@angular/router';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Meta } from '@angular/platform-browser';
import { NgProgressRef } from 'ngx-progressbar';
import { TitleService } from './core/services/config/title.service';
import { AutoTranslateService } from './core/services/auto-translate.service';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.html',
})
export class App implements OnInit {
  @ViewChild(NgProgressRef) progressBar!: NgProgressRef;
  private router = inject(Router);
  private titleService = inject(TitleService);
  private autoTranslate = inject(AutoTranslateService);
  private doc = inject(DOCUMENT);
  private meta = inject(Meta);
  private platformId = inject(PLATFORM_ID);

  constructor() {}

  ngOnInit(): void {
    const isBrowser = isPlatformBrowser(this.platformId);

    // Traducción automática, splash y hostname solo existen en el navegador: en el
    // prerender (Node) no hay IP, `requestAnimationFrame` ni `window`.
    if (isBrowser) this.autoTranslate.init();
    this.setCanonical();
    if (!isBrowser) return;
    this.removeBootSplashOnFirstRender();

    // Bloquear subdominios de desarrollo
    const hostname = window.location.hostname;
    if (hostname.includes('dev-web')) {
      this.meta.addTag({ name: 'robots', content: 'noindex, nofollow' });
    }
  }

  private setCanonical(): void {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((e: NavigationEnd) => {
      let link = this.doc.querySelector<HTMLLinkElement>("link[rel='canonical']");
      if (!link) {
        link = this.doc.createElement('link');
        link.setAttribute('rel', 'canonical');
        this.doc.head.appendChild(link);
      }
      link.setAttribute('href', `https://arcadia-viajes.com${e.urlAfterRedirects}`);
    });
  }

  /**
   * El splash de index.html (#boot-splash) solo existe cuando <app-root> llega vacío (SPA/CSR,
   * p. ej. dev-web sin la regla de .htaccess): cubre la portada hasta que la primera ruta termina
   * de pintarse, porque quitarlo antes deja un destello en blanco.
   *
   * Con la portada prerenderizada NO hay splash: la hidratación reutiliza el DOM del servidor,
   * así que no hay redibujado que tapar. Por eso tampoco se espera la hoja de estilos global
   * (waitForGlobalStyles): sin redibujado no hay riesgo de ver la página sin estilos, y en las
   * rutas CSR esa hoja es bloqueante y ya está cargada cuando arranca Angular.
   */
  private removeBootSplashOnFirstRender(): void {
    if (!this.doc.getElementById('boot-splash')) return;
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError),
      take(1)
    ).subscribe(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => this.doc.getElementById('boot-splash')?.remove()));
    });
  }

  checkRouteChange(routerEvent: Event) {
    if (routerEvent instanceof NavigationStart) {
      this.progressBar.start();
    }
    if (
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    ) {
      setTimeout(() => {
        this.progressBar.complete();
      }, 200);
    }
  }
}